import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CFormInput,
  CInputGroup,
  CRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilCheckCircle,
  cilLink,
  cilNewspaper,
  cilPlus,
  cilReload,
  cilSearch,
  cilWarning,
} from '@coreui/icons'
import Table from './Table'
import AddForm from './AddForm'
import EditForm from './EditForm'
import ArticleService from 'src/service/ArticleService'
import './Article.css'

const Article = () => {
  const [users, setUsers] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [columnSorter, setColumnSorter] = useState({})
  const [searchInput, setSearchInput] = useState('')
  const [titleFilter, setTitleFilter] = useState('')
  const [addVisible, setAddVisible] = useState(false)
  const [editArticleId, setEditArticleId] = useState(null)

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    setError(false)
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    if (titleFilter) params.append('title', titleFilter)
    if (columnSorter && columnSorter.column !== undefined) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    try {
      const result = await ArticleService.getArticle(offset, itemsPerPage, activePage, params)
      setUsers(result.data)
    } catch (e) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [activePage, itemsPerPage, columnSorter, titleFilter])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  // Debounce the search box, same as the other refreshed listings.
  useEffect(() => {
    const timer = setTimeout(() => {
      const term = searchInput.trim()
      if (titleFilter === term) return
      setActivePage(1)
      setTitleFilter(term)
    }, 350)
    return () => clearTimeout(timer)
  }, [searchInput, titleFilter])

  const { activeCount, linkedCount } = useMemo(() => {
    const articles = users?.data || []
    return {
      activeCount: articles.filter((a) => a.is_active === 1).length,
      linkedCount: articles.filter((a) => !!a.link).length,
    }
  }, [users])

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div className="ge-page-header mb-0">
          <h1 className="ge-page-title">Articles</h1>
          <p className="ge-page-subtitle">Create and manage the articles shown in the app.</p>
        </div>
        <CButton color="primary" onClick={() => setAddVisible(true)}>
          <CIcon icon={cilPlus} className="me-1" />
          Add Article
        </CButton>
      </div>

      {error && (
        <CAlert color="danger" className="d-flex flex-wrap align-items-center gap-2">
          <CIcon icon={cilWarning} className="flex-shrink-0" />
          <span>We couldn&apos;t load your articles.</span>
          <CButton
            color="danger"
            variant="outline"
            size="sm"
            className="ms-auto"
            onClick={fetchUsers}
          >
            <CIcon icon={cilReload} className="me-1" />
            Retry
          </CButton>
        </CAlert>
      )}

      <CRow className="g-3 mb-3">
        <CCol sm={6} lg={4}>
          <CCard className="at-stat-card h-100">
            <CCardBody className="d-flex align-items-center gap-3">
              <span className="at-stat-icon at-stat-icon--primary">
                <CIcon icon={cilNewspaper} size="lg" />
              </span>
              <div>
                <div className="at-stat-label">Total Articles</div>
                <div className="at-stat-value">{users?.total ?? 0}</div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="at-stat-card h-100">
            <CCardBody className="d-flex align-items-center gap-3">
              <span className="at-stat-icon at-stat-icon--success">
                <CIcon icon={cilCheckCircle} size="lg" />
              </span>
              <div>
                <div className="at-stat-label">Active on This Page</div>
                <div className="at-stat-value">{activeCount}</div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="at-stat-card h-100">
            <CCardBody className="d-flex align-items-center gap-3">
              <span className="at-stat-icon at-stat-icon--info">
                <CIcon icon={cilLink} size="lg" />
              </span>
              <div>
                <div className="at-stat-label">With External Link</div>
                <div className="at-stat-value">{linkedCount}</div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CCard>
        <CCardBody>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <CInputGroup className="ge-search">
              <span className="ge-search-icon">
                <CIcon icon={cilSearch} />
              </span>
              <CFormInput
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by title..."
                aria-label="Search articles by title"
              />
            </CInputGroup>
          </div>

          <Table
            users={users}
            fetchUsers={fetchUsers}
            setActivePage={setActivePage}
            setItemsPerPage={setItemsPerPage}
            setColumnSorter={setColumnSorter}
            itemsPerPage={itemsPerPage}
            activePage={activePage}
            columnSorter={columnSorter}
            loading={loading}
            onEditArticle={setEditArticleId}
          />
        </CCardBody>
      </CCard>

      <AddForm visible={addVisible} onClose={() => setAddVisible(false)} fetchUsers={fetchUsers} />

      <EditForm
        articleId={editArticleId}
        visible={!!editArticleId}
        onClose={() => setEditArticleId(null)}
        fetchUsers={fetchUsers}
      />
    </>
  )
}

export default Article
